import { useMemo } from 'react';
import { useBasketAvailability } from '../bff-api/baskets/useBasketAvailability';
import { useCartState } from '../pages/cart/contexts/useCartState';
import { useBasketProductState } from '../pages/cart/hooks/useBasketProductState';
import { isBopis, isExtend, isExtendPostPurchase, isSDD } from '../utils/product-utils';
import { useCart } from './useCart';
import type { ExtendProductItem, ProductItemEditor } from '../bff-api/baskets/model';
import type { GetStateForProduct } from '../pages/cart/hooks/useBasketProductState';

type UseProductAvailabilityMessagesProps = {
    productItem: ProductItemEditor;
};

type ProductMessages = {
    showMessage: boolean;
    availableToSell: number;
    hasEnoughStock: boolean;
    isPartOfMultipleGiftRegistries: boolean;
    isBopisNotEligible: boolean;
    isSDDDisabled: boolean;
    isBopisDisabled: boolean;
    isSDDStoreNotAvailable: boolean;
    isBopisStoreNotAvailable: boolean;
    isSDDStoreOutOfStock: boolean;
    isBopisStoreOutOfStock: boolean;
    showSDDWithGRItemMessage?: boolean | null;
    associatedProductAvailability?: number | null;
};

export const useProductAvailabilityMessages = ({
    productItem,
}: UseProductAvailabilityMessagesProps): ProductMessages => {
    const cart = useCart();
    const { editorProductItems } = useCartState();
    const { data: availability, isLoading, isFetching } = useBasketAvailability();
    const sddStoreAvailable = !availability || availability.sddStoreAvailable;
    const bopisStoreAvailable = !availability || availability.bopisStoreAvailable;
    const sddEnabled = !availability || availability.sddEnabled;
    const bopisEnabled = !availability || availability.bopisEnabled;

    const { getStateForProduct, getFlagsForProduct } = useBasketProductState();

    const productState = getStateForProduct(productItem.productId);
    const productFlags = getFlagsForProduct(productItem.productId, productItem.quantityEditor);

    const { availableToSell, hasEnoughStock, associatedProductAvailability } = useMemo(() => {
        if (isExtend(productItem)) {
            return calculateExtendAvailability(productItem, editorProductItems, getStateForProduct);
        }

        if (isExtendPostPurchase(productItem)) {
            const extendAvailability = calculateExtendPostPurchaseAvailability(productItem);
            return { ...extendAvailability, associatedProductAvailability: null };
        }

        if (isFetching && !productState?.availability) {
            return {
                availableToSell: 99,
                hasEnoughStock: true,
                associatedProductAvailability: null,
            };
        }

        const defaultAvailableToSell = productState?.availability?.availableToSell ?? 0;
        const defaultHasEnoughStock = productFlags?.hasEnoughStock ?? true;

        return {
            availableToSell: defaultAvailableToSell,
            hasEnoughStock: defaultHasEnoughStock,
            associatedProductAvailability: null,
        };
    }, [
        getStateForProduct,
        editorProductItems,
        productItem,
        productState,
        productFlags,
        isFetching,
    ]);

    const {
        isPartOfMultipleGiftRegistries,
        isBopisNotEligible,
        isSDDDisabled,
        isBopisDisabled,
        isSDDStoreNotAvailable,
        isBopisStoreNotAvailable,
        isSDDStoreOutOfStock,
        isBopisStoreOutOfStock,
        showSDDWithGRItemMessage,
    } = useMemo(() => {
        if (isLoading) {
            return {
                isPartOfMultipleGiftRegistries: false,
                showSDDWithGRItemMessage: false,
                isBopisNotEligible: false,
                isSDDDisabled: false,
                isBopisDisabled: false,
                isSDDStoreNotAvailable: false,
                isBopisStoreNotAvailable: false,
                isSDDStoreOutOfStock: false,
                isBopisStoreOutOfStock: false,
            };
        }
        const isPartOfMultipleGiftRegistriesInner =
            cart.hasMultipleGiftRegistries && !!productItem.c_GiftRegistryLineItem;

        const showSDDWithGRItemMessageInner =
            !!cart.countSameDayDelivery &&
            !!cart.countGiftRegistry &&
            productItem.c_isSameDayDelivery;

        const isSDDNotAvailableInner = isSDD(productItem) && !sddStoreAvailable;

        const isSDDDisabledInner = isSDD(productItem) && !sddEnabled;

        const isSDDStoreOutOfStockInner = isSDD(productItem) && availableToSell <= 0;

        const isBopisNotAvailableInner = isBopis(productItem) && !bopisStoreAvailable;

        const isBopisDisabledInner = isBopis(productItem) && !bopisEnabled;

        const isBopisStoreOutOfStockInner = isBopis(productItem) && availableToSell <= 0;

        const isBopisNotEligibleInner =
            (isBopis(productItem) || isSDD(productItem)) &&
            !productState?.product?.c_isBopisEligible;

        return {
            isPartOfMultipleGiftRegistries: isPartOfMultipleGiftRegistriesInner,
            showSDDWithGRItemMessage: showSDDWithGRItemMessageInner,
            isBopisNotEligible: isBopisNotEligibleInner,
            isSDDDisabled: isSDDDisabledInner,
            isBopisDisabled: isBopisDisabledInner,
            isSDDStoreNotAvailable: isSDDNotAvailableInner,
            isBopisStoreNotAvailable: isBopisNotAvailableInner,
            isSDDStoreOutOfStock: isSDDStoreOutOfStockInner,
            isBopisStoreOutOfStock: isBopisStoreOutOfStockInner,
        };
    }, [
        cart,
        sddEnabled,
        bopisEnabled,
        sddStoreAvailable,
        bopisStoreAvailable,
        productItem,
        productState,
        availableToSell,
        isLoading,
    ]);

    const showMessage = useMemo(
        () =>
            !hasEnoughStock ||
            isPartOfMultipleGiftRegistries ||
            showSDDWithGRItemMessage ||
            isSDDStoreNotAvailable ||
            isSDDStoreOutOfStock ||
            isBopisStoreNotAvailable ||
            isBopisStoreOutOfStock ||
            isBopisNotEligible,
        [
            hasEnoughStock,
            isPartOfMultipleGiftRegistries,
            showSDDWithGRItemMessage,
            isSDDStoreNotAvailable,
            isSDDStoreOutOfStock,
            isBopisStoreNotAvailable,
            isBopisStoreOutOfStock,
            isBopisNotEligible,
        ],
    );

    return {
        showMessage,
        availableToSell,
        hasEnoughStock,
        isPartOfMultipleGiftRegistries,
        showSDDWithGRItemMessage,
        isBopisNotEligible,
        isSDDDisabled,
        isBopisDisabled,
        isSDDStoreNotAvailable,
        isBopisStoreNotAvailable,
        isSDDStoreOutOfStock,
        isBopisStoreOutOfStock,
        associatedProductAvailability,
    };
};

type ProductAvailability = {
    availableToSell: number;
    hasEnoughStock: boolean;
    associatedProductAvailability?: number;
};

const calculateExtendAvailability = (
    productItem: ProductItemEditor & ExtendProductItem,
    editorProductItems: ProductItemEditor[],
    getStateForProduct: GetStateForProduct,
): ProductAvailability => {
    let availableToSell = 0;
    let hasEnoughStock = true;

    const associatedProductSelectedQuantity = editorProductItems.find(
        (epi) => epi.productId === productItem.c_ExtendAssociation,
    )?.quantityEditor;

    const associatedProductState = getStateForProduct(productItem.c_ExtendAssociation);
    const associatedProductAvailability = associatedProductState?.availability?.availableToSell;

    if (associatedProductAvailability === 0) {
        availableToSell = 0;
        hasEnoughStock = false;
    } else {
        availableToSell = associatedProductSelectedQuantity ?? 0;
        hasEnoughStock = productItem.quantityEditor <= availableToSell;
    }

    return { availableToSell, hasEnoughStock, associatedProductAvailability };
};

const calculateExtendPostPurchaseAvailability = (
    productItem: ProductItemEditor,
): ProductAvailability => {
    const availableToSell = productItem.c_leadQuantity ?? 0;
    const hasEnoughStock = productItem.quantityEditor <= availableToSell;

    return { availableToSell, hasEnoughStock };
};
